import { Crs } from "@evergis/sgis/es/Crs";
import { Polygon } from "@evergis/sgis/es/features/Polygon";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { Arc } from "@evergis/sgis/es/renders/Arc";
import { FillStyle, PolyRender } from "@evergis/sgis/es/renders/Poly";
import { Render } from "@evergis/sgis/es/renders/Render";
import { EditorPolySymbolParams, polySymbolParams, ShadowedPolySymbol } from "./ShadowedPolySymbol";

export type EditorCircleSymbolParams = {
  centerPointRadius?: number;
  radiusStrokeWidth?: number;
  arrowHeight?: number;
  arrowWidth?: number;
} & EditorPolySymbolParams;

export class ShadowedCircleSymbol extends ShadowedPolySymbol implements EditorCircleSymbolParams {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  centerPointRadius?: number = 4;

  radiusStrokeWidth?: number = 2;

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  arrowHeight?: number = 12;

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  arrowWidth?: number = 12;

  clone() {
    return new ShadowedCircleSymbol(this.options);
  }

  public renderFunction(feature: Polygon, resolution: number, crs: Crs, painter: DomPainter): Render[] {
    const polyRenders = super.renderFunction(feature, resolution, crs, painter);
    const radiusRenders = this.getRadiusRenders(feature, resolution, crs);

    return [...polyRenders, ...radiusRenders];
  }

  // eslint-disable-next-line max-lines-per-function
  protected getRadiusRenders(feature: Polygon, resolution: number, crs: Crs): Render[] {
    const { centroid, bbox } = feature;
    const [[centroidCoordinates]] = super.getRenderedCoordinates(new Polygon([centroid], { crs }), resolution, crs);
    const [[[xMaxCoordinates]]] = super.getRenderedCoordinates(new Polygon([[bbox.xMax, 0]], { crs }), resolution, crs);
    const { centerPointRadius } = this;
    const strokeWidth = this.radiusStrokeWidth;
    const triangleHeight = this.arrowHeight;
    const triangleWidth = this.arrowWidth;
    const lineCoordinates = [
      [
        [centroidCoordinates[0] + centerPointRadius + strokeWidth, centroidCoordinates[1]],
        [xMaxCoordinates - triangleWidth, centroidCoordinates[1]],
      ],
    ];
    const triangleCoordinates = [
      [
        [xMaxCoordinates - triangleWidth, centroidCoordinates[1] - triangleHeight / 2],
        [xMaxCoordinates - triangleWidth, centroidCoordinates[1] + triangleHeight / 2],
        [xMaxCoordinates, centroidCoordinates[1]],
      ],
    ];

    return [
      new Arc(centroidCoordinates, {
        fillColor: "transparent",
        strokeColor: "black",
        strokeWidth,
        radius: centerPointRadius,
        mapCoordinates: centroid,
      }),
      new PolyRender(lineCoordinates, {
        enclosed: true,
        fillStyle: FillStyle.Color,
        fillColor: "black",
        strokeColor: "black",
        strokeWidth,
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        lineDash: [2, 8],
        lineCap: "square",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        mapCoordinates: lineCoordinates.map(rings =>
          rings.map(point => [point[0] * resolution, point[1] * -resolution]),
        ),
      }),
      new PolyRender(triangleCoordinates, {
        enclosed: true,
        fillStyle: FillStyle.Color,
        fillColor: "black",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        mapCoordinates: triangleCoordinates.map(rings =>
          rings.map(point => [point[0] * resolution, point[1] * -resolution]),
        ),
      }),
    ];
  }
}

export const DEFAULT_CREATE_CIRCLE_OPTIONS = {
  ...polySymbolParams,
  fillColor: "rgba(255, 255, 255, 0.56)",
  enclose: true,
  showVertex: false,
};

export const circleCreateSymbol = new ShadowedCircleSymbol(DEFAULT_CREATE_CIRCLE_OPTIONS);
